import { withProcedureWraper } from "../../../..";
import {
  ApprovalHistory,
  Box,
  BoxSection,
  ButtonList,
  Chats,
  Error,
  Icon,
  Loading,
  Steps,
} from "../../../../../../components";
import { useHistory } from "react-router-dom";
import { Col, Form, Input, Row, Spin, notification } from "antd";
import BoxSectionItem from "../../../../../../components/box-section/box-section-item";
import { useMTPSRequest, useMTPSSteps, useUpdateMTPS } from "../../hooks";
import omit from "lodash/omit";
import flattenDeep from "lodash/flattenDeep";

import { StringHelper } from "../../../../../../helper";
import { useMemo, useState } from "react";
import {
  DepartmentCode,
  Page,
  RequestStatus,
} from "../../../../../../constants";
import { useQueryClient } from "@tanstack/react-query";
import { IAPItem } from "../../../../../../hook";
import { EditCourseTable } from "../../../monthly-training-plan/components";
import { getPageURL } from "../../../../../../services/serviceHelper";

const MTPSDetailPage = withProcedureWraper(() => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [reason, setReason] = useState("");
  const [form] = Form.useForm();

  const {
    data = {},
    isFetching: isRequestFetching,
    isError,
  } = useMTPSRequest();
  const { mutate, isLoading: isUpdateLoading } = useUpdateMTPS();
  const { getStepItems, getApprovalHistory, approve, updateSteps, sendMailAP } =
    useMTPSSteps();
  const { isApprovedUser, stepNumber, stepStatus, userStep } = approve(
    data?.eApprovalProcess
  );

  const transformTrainingCourses = () => {
    const eTrainingCoursesForm = form.getFieldValue("eTrainingCourses");
    const eContent = data.eContent;
    return eContent.map((item: any) => {
      let newTrainingCourses: any[] = [];
      eTrainingCoursesForm.forEach((el: any) => {
        if (el.parentId === item.id) {
          newTrainingCourses.push(omit(el, ["parentId"]));
        }
      });
      return { ...item, eTrainingCourses: newTrainingCourses };
    });
  };

  const handleApprove = () => {
    const newContent = transformTrainingCourses();
    const newStep: IAPItem = {
      ...userStep,
      status: RequestStatus.approved,
      reason,
    };
    const newApprovalProcess = updateSteps(data?.eApprovalProcess, newStep);
    const updatedFields = {
      eApprovalProcess: newApprovalProcess,
      eRequestStatus: userStep.requestStatus,
      eContent: JSON.stringify(newContent),
    };
    mutate(
      { id: data.id, body: updatedFields },
      {
        onSuccess(res: any) {
          queryClient.invalidateQueries({
            queryKey: ["MTPS-requests"],
          });
          sendMailAP(res.eApprovalProcess, {
            toPath: `${getPageURL(Page.monthlyTrainingPlanSummary)}/${res.id}`,
            type: RequestStatus.approved,
          });
          notification.success({
            message: "Thành công!",
            description: `Duyệt yêu cầu thành công.`,
          });
        },
        onError() {
          notification.error({
            message: "Thất bại",
            description: `Duyệt yêu cầu thất bại.`,
          });
        },
      }
    );
  };
  const handleReject = () => {
    const newContent = transformTrainingCourses();
    const newApprovalProcess = updateSteps(data?.eApprovalProcess, {
      ...userStep,
      status: RequestStatus.rejected,
      reason,
    });
    const updatedFields = {
      eApprovalProcess: newApprovalProcess,
      eRequestStatus: RequestStatus.rejected,
      eContent: JSON.stringify(newContent),
    };
    mutate(
      { id: data.id, body: updatedFields },
      {
        onSuccess(res: any) {
          queryClient.invalidateQueries({
            queryKey: ["MTPS-requests"],
          });
          sendMailAP(res.eApprovalProcess, {
            toPath: `${getPageURL(Page.monthlyTrainingPlanSummary)}/${res.id}`,
            type: RequestStatus.rejected,
          });
          notification.success({
            message: "Thành công!",
            description: `Từ chối yêu cầu thành công.`,
          });
        },
        onError() {
          notification.error({
            message: "Thất bại",
            description: `Từ chối yêu cầu thất bại.`,
          });
        },
      }
    );
  };

  const allTrainingCourses = flattenDeep(
    data?.eContent?.map((item: any) =>
      item?.eTrainingCourses.map((e: any) => ({ ...e, parentId: item.id }))
    )
  );

  const isEditingTrainingCourses =
    [0, 1].includes(stepNumber) && stepStatus !== "error" && isApprovedUser;

  const disabledFields = useMemo(() => {
    if (stepNumber === 1) return [];
    return [
      "eTrainingCourse",
      "eMajor",
      "eSubject",
      "eTrainingType",
      "eTraineeObject",
    ];
  }, [stepNumber]);

  const departmentText = useMemo(() => {
    if (data?.eDepartmentCode === DepartmentCode.TSN) {
      return "Khối cơ quan/CN Tân Sơn Nhất";
    }
    if (data?.eDepartmentCode === DepartmentCode.NB) {
      return "CN Nội Bài";
    }
    if (data?.eDepartmentCode === DepartmentCode.DN) {
      return "CN Đà Nẵng";
    }
    return "";
  }, [data?.eDepartmentCode]);

  if (isRequestFetching) return <Loading />;
  if (isError) return <Error description="Không tìm thấy dữ liệu" />;

  return (
    <Spin spinning={isUpdateLoading}>
      <div className="pd15">
        <div className="PageHead flex-center">
          <div className="title" style={{ margin: 0 }}>
            <div className="btnBack" onClick={() => history.goBack()}>
              <Icon.ArrowLeft />
            </div>
            Xem xét yêu cầu phê duyệt
          </div>
        </div>
        <Box title="Quy trình phê duyệt">
          <Steps
            items={getStepItems(data?.eApprovalProcess)}
            current={stepNumber}
            status={stepStatus}
          />
        </Box>
        <Box title="Thông tin">
          <BoxSection title="Thông tin chung">
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <BoxSectionItem
                  textLeft="Họ và tên người yêu cầu"
                  textRight={
                    StringHelper.verticalBarSplit<string[]>(data?.eCreatedBy)[1]
                  }
                />
              </Col>
              <Col span={12}>
                <BoxSectionItem
                  ellipsis
                  textLeft="Cơ quan đơn vị"
                  textRight={departmentText}
                />
              </Col>
            </Row>
          </BoxSection>

          <BoxSection title="Thông tin chi tiết">
            <Form
              form={form}
              initialValues={{ eTrainingCourses: allTrainingCourses }}
            >
              <Form.Item name="eTrainingCourses">
                <EditCourseTable
                  isEditing={isEditingTrainingCourses}
                  disabledFields={disabledFields}
                />
              </Form.Item>
            </Form>
          </BoxSection>
          <BoxSection title="Lịch sử phê duyệt">
            <ApprovalHistory
              items={getApprovalHistory(data?.eApprovalProcess)}
            />
            {isApprovedUser && (
              <div className="pd15">
                <Input.TextArea
                  placeholder="Nhập lý do..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}
          </BoxSection>
          <BoxSection title="Thông tin trao đổi">
            <Chats
              items={data?.eComments}
              mutateFn={mutate}
              requestId={data.id}
            />
          </BoxSection>
        </Box>
        <div className="flex flex-center">
          <ButtonList
            items={[
              {
                hidden: !isApprovedUser,
                buttonType: "success",
                label: "Đồng ý",
                onClick: handleApprove,
              },
              {
                hidden: !isApprovedUser,
                buttonType: "grey",
                label: "Không đồng ý",
                onClick: handleReject,
              },
            ]}
          />
        </div>
      </div>
    </Spin>
  );
});

export default MTPSDetailPage;
